<script lang="ts">
  import type { GatFormFieldProps } from '../gat-form-field'

  export type GatTextAreaModelValue = string | null | undefined

  export interface GatTextAreaProps
    extends GatFormFieldProps<GatTextAreaModelValue> {
    /** The value of the input */
    modelValue?: GatTextAreaModelValue
    /** If the input should be focused when the component is mounted */
    autofocus?: boolean
    /** Input placeholder if the input is empty */
    placeholder?: string
    /** Forces input-value to be upper-case */
    upperCase?: boolean
    /** Forces input-value to be lower-case */
    lowerCase?: boolean
    /** Forces the maximum allowed characters for the input */
    maxLength?: number
  }

  export type GatTextAreaEmits = {
    /** Emitted when the input value is changed */
    (e: 'update:modelValue', value: GatTextAreaModelValue): void
    /** Emitted when the input loses focus */
    (e: 'blur', event: FocusEvent): void
    /** Emitted when the input value is pasted */
    (e: 'paste', event: ClipboardEvent): void

    (e: 'paste-text', text: string | undefined): void
  }
</script>

<script setup lang="ts">
  import { nextTick, onMounted, useTemplateRef } from 'vue'
  import { useTextAreaModelValue } from './useAreaEditModelValue'
  import { GatFormField } from '../gat-form-field'
  import { cn } from '../../lib/utils'

  const props = withDefaults(defineProps<GatTextAreaProps>(), {
    showOptional: undefined,
    validateOn: 'input',
    hideDetails: 'auto',
    autocomplete: 'off'
  })

  const emit = defineEmits<GatTextAreaEmits>()

  const loading = defineModel<boolean>('loading', {
    default: false,
    required: false
  })

  const formField = useTemplateRef('form-field')

  const { textEditValue } = useTextAreaModelValue(props, emit)
  const input = useTemplateRef('input')

  function handlePaste(event: ClipboardEvent) {
    emit('paste', event)
    const text = event?.clipboardData?.getData('text')
    emit('paste-text', text)
  }

  onMounted(() => {
    if (props.autofocus) {
      nextTick(() => {
        //   input.value?.focus()
      })
    }
  })

  defineExpose({
    validate: formField.value?.validate(),
    loading,
    input
  })
</script>

<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    :value="textEditValue"
    @clear="emit('update:modelValue', undefined)"
  >
    <template #default="{ errorMessages, isValid, name, disabled, inputClass }">
      <textarea
        ref="input"
        :id="name"
        v-model="textEditValue"
        :data-testid="dataTestid"
        :placeholder
        :disabled="disabled"
        :maxLength
        :autocomplete="autocomplete"
        :class="cn('field-sizing-content flex min-h-14 w-full', inputClass)"
        @blur="[
          emit('blur', $event),
          formField?.handleBlur($event),
          formField?.setHideDetails(
            props.hideDetails === 'auto' && errorMessages.length === 0
              ? false
              : undefined
          )
        ]"
        @paste="handlePaste($event)"
        @focus="[
          formField?.setHideDetails(
            props.hideDetails === 'auto' ? true : undefined
          )
        ]"
      />
    </template>
    <template #append-inner>
      <slot name="append-inner" />
    </template>
  </gat-form-field>
</template>
